import { useEffect, useMemo, type FC } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { useLazyQuery, useMutation } from "@apollo/client"

import styles from "./KnownIssueCreatePage.module.scss"
import KnownIssueForm from "./KnownIssueForm"

import { graphql } from "@/souhl_admin/gql/__generated__/gql"
import type {
	KnownIssueFormData,
	LogModuleName,
} from "@/souhl_admin/utils/forms/knownIssue"

const GetKnownIssueForEdit = graphql(/* GraphQL */ `
	query GetKnownIssueForEdit($id: String!) {
		knownIssue(id: $id) {
			knownIssue {
				id
				description
				issueType
				logLevel
				logMatchMethod
				logKeyword
				logModuleName
				logKind
				logSalonIds
				slackChannelNameToNotify
				mentionTargets {
					slackMentionTargetId
				}
				thresholdCountToNotify
				cooldownHoursToNotify
				autoResolveDaysAfterLastActivity
				shouldSendIosCriticalNotification
			}
		}
	}
`)

const UpdateKnownIssue = graphql(/* GraphQL */ `
	mutation UpdateKnownIssue($input: UpdateKnownIssueInput!) {
		updateKnownIssue(input: $input) {
			knownIssue {
				id
			}
		}
	}
`)

const KnownIssueEditPage: FC = () => {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()

	const [fetchKnownIssue, { data, loading: fetchLoading, error: fetchError }] =
		useLazyQuery(GetKnownIssueForEdit)

	useEffect(() => {
		if (id) {
			fetchKnownIssue({ variables: { id }, fetchPolicy: "network-only" })
		}
	}, [id, fetchKnownIssue])

	const [updateKnownIssue, { loading: updateLoading }] = useMutation(
		UpdateKnownIssue,
		{
			onCompleted: () => {
				navigate(`/souhl_admin/known_issues/${id}`)
			},
			onError: (error) => {
				console.error("Failed to update known issue:", error)
				alert(`更新に失敗しました: ${error.message}`)
			},
		}
	)

	const issue = data?.knownIssue.knownIssue

	// Convert fetched data to form format (memoized to prevent re-renders)
	const initialData = useMemo((): Partial<KnownIssueFormData> | undefined => {
		if (!issue) return undefined

		return {
			description: issue.description,
			issueType: issue.issueType,
			logLevel: issue.logLevel,
			logMatchMethod: issue.logMatchMethod,
			logKeyword: issue.logKeyword,
			logModuleName: issue.logModuleName as LogModuleName, // TODO: as なくしたい
			logKind: issue.logKind ?? "",
			logSalonIds: issue.logSalonIds.join(", "),
			slackChannelNameToNotify: issue.slackChannelNameToNotify,
			mentionTargetIds: issue.mentionTargets.map((t) => t.slackMentionTargetId),
			thresholdCountToNotify: issue.thresholdCountToNotify,
			enableCooldown: issue.cooldownHoursToNotify != null,
			cooldownHoursToNotify: issue.cooldownHoursToNotify ?? 1,
			autoResolveDaysAfterLastActivity: issue.autoResolveDaysAfterLastActivity,
			shouldSendIosCriticalNotification:
				issue.shouldSendIosCriticalNotification,
		}
	}, [issue])

	const handleSubmit = async (data: KnownIssueFormData) => {
		if (!id) return

		await updateKnownIssue({
			variables: {
				input: {
					id,
					description: data.description,
					issueType: data.issueType,
					logLevel: data.logLevel,
					logMatchMethod: data.logMatchMethod,
					logKeyword: data.logKeyword,
					logModuleName: data.logModuleName,
					logKind: data.logKind || null,
					// 現状、parseInt が通らないやつは無視している
					// TODO: ここ重複なくしたい
					logSalonIds: data.logSalonIds
						? data.logSalonIds
								.split(",")
								.map((s) => s.trim())
								.filter((s) => s !== "")
								.map((s) => parseInt(s, 10))
								.filter((n) => !isNaN(n))
						: [],
					slackChannelNameToNotify: data.slackChannelNameToNotify,
					mentionTargetIds: data.mentionTargetIds,
					thresholdCountToNotify: data.thresholdCountToNotify,
					cooldownHoursToNotify: data.enableCooldown
						? data.cooldownHoursToNotify
						: null,
					autoResolveDaysAfterLastActivity:
						data.autoResolveDaysAfterLastActivity,
					shouldSendIosCriticalNotification:
						data.shouldSendIosCriticalNotification,
				},
			},
		})
	}

	const handleCancel = () => {
		navigate(`/souhl_admin/known_issues/${id}`)
	}

	// Loading or initial state
	if (fetchLoading || (!data && !fetchError)) {
		return (
			<div className={styles.container}>
				<div className={styles.loading}>読み込み中...</div>
			</div>
		)
	}

	// Error state
	if (fetchError || !issue) {
		return (
			<div className={styles.container}>
				<div className={styles.error}>
					{fetchError?.message || "Known Issue が見つかりません"}
				</div>
				<button
					className={styles.backButton}
					onClick={() => navigate("/souhl_admin/known_issues")}
				>
					一覧に戻る
				</button>
			</div>
		)
	}

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<h1 className={styles.title}>Known Issue 編集</h1>
			</div>

			<KnownIssueForm
				initialData={initialData}
				onSubmit={handleSubmit}
				submitLabel="更新"
				isSubmitting={updateLoading}
				onCancel={handleCancel}
			/>
		</div>
	)
}

export default KnownIssueEditPage
